'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, AlertCircle, Shield, Lock, ArrowRight } from 'lucide-react';
import { useRole } from '@/context/RoleContext';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';

interface AdminLoginFormData {
  email: string;
  password: string;
}

interface AdminFirstLoginFormProps {
  onSwitchToLogin: () => void;
}

type AdminState = 'checking' | 'no_admin' | 'has_admin' | 'error';

export default function AdminFirstLoginForm({ onSwitchToLogin }: AdminFirstLoginFormProps) {
  const router = useRouter();
  const { setRole } = useRole();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [adminState, setAdminState] = useState<AdminState>('checking');

  const { register, handleSubmit, formState: { errors }, setError } = useForm<AdminLoginFormData>();

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase.rpc('get_admin_count');
        if (error) {
          setAdminState('error');
          return;
        }
        setAdminState(data > 0 ? 'has_admin' : 'no_admin');
      } catch {
        setAdminState('error');
      }
    };
    checkAdmin();
  }, []);

  const onSubmit = async (data: AdminLoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const supabase = createClient();
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', result?.user?.id)
        .single();

      const userRole = profileData?.role;
      if (userRole !== 'admin') {
        setError('email', { message: 'This account does not have administrator privileges.' });
        return;
      }

      setRole('admin');
      toast.success('Welcome, Administrator! Redirecting to your dashboard...');
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      const msg = err?.message || 'Invalid credentials';
      if (
        msg.includes('Refresh Token Not Found') ||
        msg.includes('Invalid Refresh Token') ||
        msg.includes('refresh_token_not_found')
      ) {
        return;
      }
      if (msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many requests')) {
        setError('email', { message: 'Too many attempts. Please wait a moment and try again.' });
        return;
      }
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials') || msg.toLowerCase().includes('password')) {
        setError('email', { message: 'Invalid email or password' });
      } else {
        setError('email', { message: msg });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Checking state
  if (adminState === 'checking') {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-purple-600/30 border-t-purple-600 rounded-full animate-spin mb-4" />
        <p className="text-gray-500 text-sm">Verifying system status…</p>
      </div>
    );
  }

  // No admin exists yet — prompt to set up
  if (adminState === 'no_admin') {
    return (
      <div>
        <div className="mb-7">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
              <Shield size={18} className="text-purple-600" />
            </div>
            <h2 className="font-display font-700 text-gray-900 text-2xl">Administrator Access</h2>
          </div>
          <p className="text-gray-500 text-sm">Set up your system administrator account to get started.</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-1">No Administrator Account Found</p>
              <p className="text-xs text-amber-700 leading-relaxed">
                This system has not been configured yet. You need to create the first administrator account before anyone can manage the platform.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {[
            'Create your secure admin credentials',
            'Enter the system setup key provided by your IT team',
            'Gain full access to manage users, courses, and settings',
          ].map((step, i) => (
            <div key={`step-${i}`} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                {i + 1}
              </div>
              <p className="text-sm text-gray-600">{step}</p>
            </div>
          ))}
        </div>

        <Link
          href="/admin-setup"
          className="w-full py-2.5 bg-purple-700 hover:bg-purple-800 active:scale-95 text-white font-display font-semibold text-sm rounded-xl transition-all duration-150 flex items-center justify-center gap-2"
        >
          <Shield size={15} />
          Set Up Administrator Account
          <ArrowRight size={15} />
        </Link>

        <p className="text-center text-sm text-gray-500 mt-5">
          Not an administrator?{' '}
          <button onClick={onSwitchToLogin} className="text-teal-600 font-semibold hover:underline">
            Sign in here
          </button>
        </p>
      </div>
    );
  }

  // Error state
  if (adminState === 'error') {
    return (
      <div>
        <div className="mb-7">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
              <Shield size={18} className="text-purple-600" />
            </div>
            <h2 className="font-display font-700 text-gray-900 text-2xl">Administrator Access</h2>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">Unable to verify system status. Please try again or contact support.</p>
          </div>
        </div>
        <button
          onClick={() => setAdminState('checking')}
          className="w-full py-2.5 bg-purple-700 hover:bg-purple-800 text-white font-semibold text-sm rounded-xl transition-colors"
        >
          Retry
        </button>
        <p className="text-center text-sm text-gray-500 mt-5">
          <button onClick={onSwitchToLogin} className="text-teal-600 font-semibold hover:underline">
            Back to Sign In
          </button>
        </p>
      </div>
    );
  }

  // Admin exists — show admin login form
  return (
    <div>
      <div className="mb-7">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
            <Shield size={18} className="text-purple-600" />
          </div>
          <h2 className="font-display font-700 text-gray-900 text-2xl">Administrator Sign In</h2>
        </div>
        <p className="text-gray-500 text-sm">Sign in with your administrator credentials to access the management portal.</p>
      </div>

      <div className="flex items-center gap-2 p-3 rounded-xl bg-purple-50 border border-purple-200 mb-5">
        <Lock size={14} className="text-purple-600 flex-shrink-0" />
        <p className="text-xs text-purple-700 font-medium">Restricted access — Administrator accounts only</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="label-base">Administrator Email</label>
          <input
            type="email"
            placeholder="admin@stargalms.edu"
            className={`input-base ${errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-500/20' : 'focus:border-purple-400 focus:ring-purple-500/20'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' }
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="label-base mb-0">Password</label>
            <button type="button" className="text-xs text-purple-600 hover:underline">Forgot password?</button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className={`input-base pr-10 ${errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-500/20' : 'focus:border-purple-400 focus:ring-purple-500/20'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' }
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.password.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-purple-700 hover:bg-purple-800 active:scale-95 text-white font-display font-semibold text-sm rounded-xl transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:scale-100"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Signing in…
            </>
          ) : (
            <>
              <Shield size={15} />
              Sign In as Administrator
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-5">
        Not an administrator?{' '}
        <button onClick={onSwitchToLogin} className="text-teal-600 font-semibold hover:underline">
          Sign in here
        </button>
      </p>
    </div>
  );
}
